import { RichTextField } from '@prismicio/client';
import * as prismicH from '@prismicio/helpers';

import { ScheduleWorkingHours } from '@/types';
import { filterSliceItemsByLocationSlug, getStoreScheduleInfo } from '@/utils';

// --- Helpers ---

type Sourced<TPrismic, TConfig = TPrismic> =
  | { source: 'prismic'; value: TPrismic }
  | { source: 'config'; value: TConfig };

const filledText = (value?: string | null) => value?.trim() || null;

const isFilledRichText = (
  field?: RichTextField | null,
): field is RichTextField => Boolean(field && prismicH.asText(field).trim());

type LocationSlugRow = { location_slug?: string | null };

/**
 * Filters rows for the store and orders them: location-specific first, then
 * global. `controlRow` is the row whose hide_* flags apply (first specific
 * row, otherwise first global row).
 */
const prepareRows = <T extends LocationSlugRow>(
  rows: T[] | null | undefined,
  storeUrlSlug: string | undefined,
) => {
  const applicable: T[] = filterSliceItemsByLocationSlug(rows, storeUrlSlug);
  const specific: T[] = [];
  const global: T[] = [];

  for (const row of applicable) {
    (row?.location_slug?.trim() ? specific : global).push(row);
  }

  const ordered = specific.concat(global);
  return { applicable, ordered, controlRow: ordered[0] as T | undefined };
};

const firstText = <T>(
  rows: T[],
  read: (_row: T) => string | null | undefined,
) => {
  for (const row of rows) {
    const text = filledText(read(row));
    if (text) return text;
  }
  return null;
};

const firstRichText = <T>(
  rows: T[],
  read: (_row: T) => RichTextField | null | undefined,
) => {
  for (const row of rows) {
    const value = read(row);
    if (isFilledRichText(value)) return value;
  }
  return null;
};

/** Prismic value wins; config is computed lazily only when Prismic is empty. */
const withSource = <TPrismic, TConfig>(
  prismic: TPrismic | null,
  config: () => TConfig | null,
): Sourced<TPrismic, TConfig> | null => {
  if (prismic !== null) return { source: 'prismic', value: prismic };
  const value = config();
  return value !== null ? { source: 'config', value } : null;
};

// --- Location contact ---

export type FooterLocationStore = {
  url_slug?: string | null;
  full_address?: string | null;
  phone?: string | null;
  working_hours?: ScheduleWorkingHours[] | null;
};

export type FooterLocationContactRow = {
  hide_location_contact_info?: boolean | null;
  location_slug?: string | null;
  address?: string | null;
  hide_address?: boolean | null;
  phone?: string | null;
  hide_phone?: boolean | null;
  email?: string | null;
  hide_email?: boolean | null;
  hours?: RichTextField | null;
  hide_hours?: boolean | null;
};

export type FooterLocationTextValue = Sourced<string>;

export type FooterLocationRichValue = Sourced<
  RichTextField,
  { day: string; period: string }[]
>;

export type ResolvedFooterLocationContact = {
  hidden: boolean;
  address: string | null;
  phone: FooterLocationTextValue | null;
  email: string | null;
  hours: FooterLocationRichValue | null;
};

type LocationHideKey =
  | 'hide_address'
  | 'hide_phone'
  | 'hide_email'
  | 'hide_hours';

const HIDDEN_LOCATION: ResolvedFooterLocationContact = {
  hidden: true,
  address: null,
  phone: null,
  email: null,
  hours: null,
};

const configHours = (store?: FooterLocationStore | null) => {
  const hours = getStoreScheduleInfo(store?.working_hours ?? undefined);
  return hours.length ? hours : null;
};

/**
 * Field priority: first filled specific row, then first filled global row,
 * then the config value. With no applicable row, every field comes from config.
 */
export const resolveFooterLocationContact = (
  rows: FooterLocationContactRow[] | null | undefined,
  store?: FooterLocationStore | null,
): ResolvedFooterLocationContact => {
  const { applicable, ordered, controlRow } = prepareRows(
    rows,
    store?.url_slug ?? undefined,
  );

  if (applicable.some(row => row?.hide_location_contact_info === true)) {
    return { ...HIDDEN_LOCATION };
  }

  const isHidden = (key: LocationHideKey) => controlRow?.[key] === true;
  const text = (
    read: (_row: FooterLocationContactRow) => string | null | undefined,
  ) => firstText(ordered, read);

  return {
    hidden: false,
    address: isHidden('hide_address')
      ? null
      : text(row => row.address) ?? filledText(store?.full_address),
    phone: isHidden('hide_phone')
      ? null
      : withSource(
          text(row => row.phone),
          () => filledText(store?.phone),
        ),
    email: isHidden('hide_email') ? null : text(row => row.email),
    hours: isHidden('hide_hours')
      ? null
      : withSource(
          firstRichText(ordered, row => row.hours),
          () => configHours(store),
        ),
  };
};

// --- Licenses ---

export type FooterDispensaryLicense = {
  licenseNumber?: string | null;
  license_number?: string | null;
  customerType?: string | null;
  customer_type?: string | null;
};

export type FooterLicenseStore = {
  url_slug?: string | null;
  dispensaryLicenses?: FooterDispensaryLicense[] | null;
  dispensary_licenses?: FooterDispensaryLicense[] | null;
};

export type FooterLicenseRow = {
  location_slug?: string | null;
  licenses?: RichTextField | null;
};

export type ResolvedFooterLicenses = Sourced<RichTextField, string[]>;

const LICENSE_PREFIXES: Record<string, string> = {
  ADULT: 'Lic. Adult',
  RECREATIONAL: 'Lic. Adult',
  MEDICAL: 'Lic. Med',
};

const licensePrefix = (customerType?: string | null) =>
  LICENSE_PREFIXES[customerType?.trim().toUpperCase() ?? ''] ?? 'Lic.';

const configLicenseLines = (store?: FooterLicenseStore | null) => {
  const lines = new Set<string>();

  for (const license of store?.dispensaryLicenses ??
    store?.dispensary_licenses ??
    []) {
    const number = filledText(
      license?.licenseNumber ?? license?.license_number,
    );
    if (number) {
      const prefix = licensePrefix(
        license?.customerType ?? license?.customer_type,
      );
      lines.add(`${prefix} - ${number}`);
    }
  }

  return lines.size ? [...lines] : null;
};

/**
 * Licenses for the legal footer block. Prismic rich text wins over config
 * license numbers. Empty returns null.
 */
export const resolveFooterLicenses = (
  rows: FooterLicenseRow[] | null | undefined,
  store?: FooterLicenseStore | null,
): ResolvedFooterLicenses | null => {
  const { ordered } = prepareRows(rows, store?.url_slug ?? undefined);

  return withSource(
    firstRichText(ordered, row => row.licenses),
    () => configLicenseLines(store),
  );
};
